<template>
  <div v-if="credentials">
    <v-layout column align-center justify-center>
      <v-card
        elevation="4"
        min-width="320"
        max-width="600"
        class="mx-auto mt-12 mb-5 px-10"
      >
        <v-card-title>{{
          credentials.registered_credentials.credentials_id || "New Credentials"
        }}</v-card-title>
        <v-card-subtitle>
          Credentials are only needed for Azure and Google Cloud deliveries.
        </v-card-subtitle>
        <v-card-text>
          <v-container pa-0 @keydown.escape="cancel">
            <v-form
              @keydown.enter="submit"
              @submit.prevent="submit"
              ref="form"
              lazy-validation
            >
              <hr />
              <br />
              <v-row
                ><v-layout justify-center>
                  <v-btn
                    v-if="read_only"
                    class="ma-4 button_text--text"
                    elevation="4"
                    color="primary"
                    @click="deleteCredentials()"
                  >
                    Delete Credentials
                  </v-btn>
                  <v-btn
                    v-if="read_only"
                    class="ma-4 button_text--text"
                    elevation="4"
                    color="primary"
                    @click="flipReadOnly()"
                  >
                    Edit Credentials
                  </v-btn>
                </v-layout></v-row
              >

              <!-- Read Only View -->
              <v-row v-if="read_only">
                <v-col>
                  <div>Info</div>
                  <div v-bind:style="{ fontSize: '14px' }" class="mt-4 mx-4">
                    Credentials ID:
                    {{ credentials.registered_credentials.credentials_id }}
                    <br />
                    Description:
                    {{ credentials.registered_credentials.description }}
                    <br />
                  </div>
                </v-col>
              </v-row>

              <!-- Edit View -->
              <v-row v-if="!read_only">
                <v-col>
                  Info
                  <v-text-field
                    id="id_for_creds"
                    ref="creds_id"
                    v-model="credentials.registered_credentials.credentials_id"
                    :disabled="!creds_creation"
                    :rules="[
                      v => !!v || 'Credentials ID is required',
                      v =>
                        /^([a-zA-Z0-9-_]*$)/.test(v) ||
                        'Credientials ID must contain alphanumeric characters, underscores (_) and hyphens (-)'
                    ]"
                    outlined
                    dense
                    :autofocus="creds_creation"
                    :readonly="read_only"
                    class="mt-4"
                  >
                    <template #label>
                      Credentials ID&nbsp;<span
                        class="font-weight-bold error-text"
                        >*</span
                      >
                    </template>
                  </v-text-field>

                  <v-text-field
                    id="creds_desc"
                    :readonly="read_only"
                    v-model="credentials.registered_credentials.description"
                    label="Description"
                    outlined
                    dense
                    :autofocus="!creds_creation"
                    class="mt-1"
                  >
                  </v-text-field>

                  <v-switch
                    id="update_creds"
                    v-if="!creds_creation"
                    v-model="change_credentials"
                    label="Update Credentials"
                    dense
                  ></v-switch>

                  <div v-show="change_credentials || creds_creation">
                    Credentials
                    <v-radio-group v-model="platform" mandatory>
                      <template v-slot:label>
                        <div>
                          Select which cloud platform the credentials will be
                          registered for
                        </div>
                      </template>
                      <v-radio
                        v-on:change="
                          credentials.registered_credentials.credentials = null
                        "
                        label="Google Cloud Platform"
                        value="GCP"
                      ></v-radio>
                      <v-radio
                        v-on:change="
                          credentials.registered_credentials.credentials = null
                        "
                        label="Azure Blob Storage"
                        value="Azure"
                      ></v-radio>
                    </v-radio-group>

                    <span v-if="platform === 'GCP'" class="pl-1">
                      <div>
                        <span
                          v-bind:style="{
                            fontSize: '14px',
                            color: 'rgba(255, 255, 255, 0.7)'
                          }"
                        >
                          Upload your Google Cloud credentials JSON file
                          (non-encoded). We'll base64-encode and add the string
                          to the credentials text box&nbsp;

                          <v-btn
                            :loading="pending"
                            text
                            outlined
                            small
                            @click.prevent="onUploadClick"
                          >
                            <span>
                              <v-icon small class="pr-1"
                                >mdi-cloud-upload-outline</v-icon
                              >
                              upload</span
                            >
                          </v-btn>
                          <input
                            ref="fileInput"
                            class="d-none"
                            type="file"
                            accept=".json"
                            :disabled="pending"
                            @change="onFileChange"
                            @click="
                              /*
                               * workaround to fix issue not
                               * triggering onFileChange event
                               * when the same file is
                               * selected
                               */
                              $refs.fileInput.value = ''
                            "
                          />
                          <br /><br />
                          OR
                          <br /><br />
                          Base64-encode your credentials and paste them in the
                          text box.
                        </span>
                      </div>
                    </span>

                    <v-row>
                      <v-col>
                        <v-text-field
                          id="gcp_base"
                          v-if="platform === 'GCP'"
                          ref="credentials"
                          :readonly="read_only"
                          v-model="
                            credentials.registered_credentials.credentials
                          "
                          :rules="
                            change_credentials || creds_creation
                              ? [v => !!v || 'Credentials is required']
                              : []
                          "
                          outlined
                          dense
                        >
                          <template #label>
                            base64-encoded credentials&nbsp;<span
                              class="font-weight-bold error-text"
                              >*</span
                            >
                          </template>
                        </v-text-field>
                        <v-text-field
                          v-else
                          id="azure_sas"
                          ref="credentials"
                          :readonly="read_only"
                          v-model="
                            credentials.registered_credentials.credentials
                          "
                          :rules="
                            change_credentials || creds_creation
                              ? [v => !!v || 'Credentials is required']
                              : []
                          "
                          outlined
                          dense
                        >
                          <template #label>
                            SAS URL&nbsp;<span
                              class="font-weight-bold error-text"
                              >*</span
                            >
                          </template>
                        </v-text-field>
                      </v-col>
                    </v-row>
                  </div>
                </v-col>
              </v-row>

              <div class="text-caption text--disabled my-2">
                <span class="font-weight-bold mr-2">*</span>required field
              </div>

              <v-row v-if="!read_only"
                ><v-col
                  ><v-layout justify-center>
                    <v-btn
                      class="ma-4 ml-0"
                      elevation="4"
                      outlined
                      color="#E5E1DA"
                      @click="cancel"
                    >
                      Cancel
                    </v-btn>
                    <v-btn
                      class="ma-4 mr-0 button_text--text"
                      elevation="4"
                      color="primary"
                      type="submit"
                    >
                      {{ creds_creation ? "Create" : "Save" }} Credentials
                    </v-btn>
                  </v-layout></v-col
                ></v-row
              >
            </v-form>
          </v-container>
        </v-card-text>
      </v-card>

      <div
        class="text-caption"
        v-if="credentials.registered_credentials.created"
      >
        <span class="pr-4"
          ><span class="text--disabled">Created:</span>
          {{
            new Date(
              credentials.registered_credentials.created
            ).toLocaleString()
          }}</span
        >
        <span class="pl-4"
          ><span class="text--disabled">Last modified:</span>
          {{
            new Date(
              credentials.registered_credentials.modified
            ).toLocaleString()
          }}</span
        >
      </div>
    </v-layout>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { createNamespacedHelpers } from "vuex-composition-helpers";
const uiStore = createNamespacedHelpers("ui");

export default {
  props: ["credentials", "read_only", "creds_creation"],
  data() {
    return {
      change_credentials: false,
      platform: "GCP",
      pending: false
    };
  },
  computed: {
    ...mapGetters(["user_details", "account_details"])
  },
  setup() {
    const { SHOW_SNACKBAR } = uiStore.useMutations(["SHOW_SNACKBAR"]);
    return { SHOW_SNACKBAR };
  },
  methods: {
    cancel() {
      this.$router.push({
        name: "creds-list",
        params: {
          account_id: this.user_details.account_id
        }
      });
    },
    submit() {
      if (this.$refs.form.validate()) {
        if (this.creds_creation) {
          this.createCredentials(this.credentials);
        } else {
          this.updateCredentials(this.credentials);
        }
      }
    },
    updateCredentials(credentials) {
      this.$emit("update_credentials", {
        credentials: credentials,
        change_credentials: this.change_credentials
      });
    },
    createCredentials(credentials) {
      this.$emit("create_credentials", {
        credentials: credentials
      });
    },
    onUploadClick() {
      this.$refs.fileInput.click();
    },
    onFileChange(e) {
      const files = e.target.files || e.dataTransfer.files;
      if (files && files.length > 0) {
        this.submitFile(files[0]);
      }
    },
    submitFile(file) {
      this.pending = true;
      this.handleFile(file);
    },
    encode(e) {
      try {
        this.credentials.registered_credentials.credentials = btoa(e);
        this.pending = false;
      } catch {
        this.alertError();
        this.pending = false;
      }
    },
    handleFile(file) {
      try {
        var fr = new FileReader();

        fr.onload = () => this.encode(fr.result);
        fr.readAsText(file);
      } catch {
        this.alertError();
        this.pending = false;
      }
    },
    alertError() {
      this.SHOW_SNACKBAR({
        text: `Failed to load and encode JSON credentials file.`,
        color: "error"
      });
    }
  }
};
</script>
<style lang="scss">
.v-application .error--text {
  color: #df323b !important;
}
</style>
